import { useEffect, useMemo, useState } from 'react'
import parse from 'html-react-parser'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Collapse from '@mui/material/Collapse'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Theme } from '@mui/material/styles'

import CloseIcon from '@mui/icons-material/Close'
import LaunchIcon from '@mui/icons-material/Launch'
import NotesIcon from '@mui/icons-material/Notes'

import config from 'src/config'
import { post } from 'src/functions/fetch'
import productUrl from 'src/functions/productUrl'
import { tProduct, tProductPreview, isProduct } from 'src/shared/frontendTypes/product'
import ImagesCarousel from '../ImagesCarousel/ImagesCarousel'
import AddToCart from '../AddToCart/AddToCart'
import AiGeneratedBadge from 'src/components/AiGeneratedBadge'
import getCombination from '../../functions/getCombination'
import getImageUrl from '../../functions/imageUrl'
import getImageAlt from '../../functions/imageAlt'
import { selectedAttributes } from '../AddToCart/Components/AttributesSelector'

const buildSelectedAttributes = (
  product: tProduct,
  preferredOptions: number[] = [],
): selectedAttributes =>
  Object.fromEntries(
    (product.attributes || []).map(attribute => {
      const preferredOption = attribute.options.find(option => preferredOptions.includes(option.id))
      const fallbackOption = attribute.options.length === 1
        ? attribute.options[0]
        : undefined

      return [attribute.id, preferredOption?.id || fallbackOption?.id || '']
    }),
  )

const getPreferredOptionsForPreview = (
  product: tProduct,
  preview: tProductPreview,
): number[] => {
  const combinationByKey = product.combinations.find(combination => combination.key === preview.key)
  if (combinationByKey?.options?.length) return combinationByKey.options

  const previewOptions = preview.options || []
  if (previewOptions.length === 0) return []

  const combinationByOptions = product.combinations.find(combination =>
    previewOptions.every(optionId => combination.options.includes(optionId)),
  )

  return combinationByOptions?.options || previewOptions
}

const mergePreviewCommerceIntoProduct = (
  product: tProduct,
  preview: tProductPreview,
): tProduct => {
  const combinationIndex = product.combinations.findIndex(combination => combination.key === preview.key)
  if (combinationIndex < 0) return product

  const currentCombination = product.combinations[combinationIndex]
  const combinations = product.combinations.slice()
  const hasPreviewDiscount = Object.prototype.hasOwnProperty.call(preview, 'discount')

  combinations[combinationIndex] = {
    ...currentCombination,
    price: preview.price ?? currentCombination.price,
    deliverytime: preview.deliverytime ?? currentCombination.deliverytime,
    stock: typeof preview.stock === 'number' ? preview.stock : currentCombination.stock,
    discount: hasPreviewDiscount ? preview.discount : currentCombination.discount,
  }

  return {
    ...product,
    inquireOnly: preview.inquireOnly ?? product.inquireOnly,
    combinations,
  }
}

const CrossSellProductModal: React.FC<{
  open: boolean
  preview: tProductPreview | null
  currentProduct: tProduct
  prefetchedProduct?: tProduct | null
  onClose: () => void
}> = ({
  open,
  preview,
  currentProduct,
  prefetchedProduct,
  onClose,
}) => {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))
  const [modalProduct, setModalProduct] = useState<tProduct | null>(null)
  const [loading, setLoading] = useState(false)
  const [descriptionOpen, setDescriptionOpen] = useState(false)
  const [selectedAttributes, setSelectedAttributes] = useState<selectedAttributes>({})

  useEffect(() => {
    if (!open || !preview) return

    const controller = new AbortController()
    const applyProduct = (product: tProduct) => {
      const productWithPreviewCommerce = mergePreviewCommerceIntoProduct(product, preview)
      const preferredOptions = getPreferredOptionsForPreview(productWithPreviewCommerce, preview)

      setModalProduct(productWithPreviewCommerce)
      setSelectedAttributes(buildSelectedAttributes(productWithPreviewCommerce, preferredOptions))
      setDescriptionOpen(false)
    }

    if (preview.id === currentProduct.id) {
      setLoading(false)
      applyProduct(currentProduct)
      return () => controller.abort()
    }

    if (prefetchedProduct && prefetchedProduct.id === preview.id) {
      setLoading(false)
      applyProduct(prefetchedProduct)
      return () => controller.abort()
    }

    setLoading(true)
    setModalProduct(null)

    post({
      url: `product/${preview.id}`,
      body: {
        ...config.shop,
        skipAlsoPurchased: true,
        skipCrossSellSections: true,
      },
      signal: controller.signal,
    }).then(data => {
      if (!isProduct(data) || controller.signal.aborted) {
        if (!controller.signal.aborted) setModalProduct(null)
        return
      }

      applyProduct(data)
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false)
    })

    return () => controller.abort()
  }, [currentProduct, open, prefetchedProduct, preview])

  const combination = useMemo(
    () => modalProduct
      ? getCombination(modalProduct, selectedAttributes)
      : null,
    [modalProduct, selectedAttributes],
  )

  const images = combination?.images || modalProduct?.images || []

  const handleChangeAttribute = (attribute: number, option: number | '') =>
    setSelectedAttributes(prev => ({ ...prev, [attribute]: option }))

  const handleOpenArticle = () => {
    if (!preview) return

    const selectedOptions = combination?.options
      || Object.values(selectedAttributes).filter((option): option is number => typeof option === 'number')
      || preview.options
      || []

    const href = productUrl(
      modalProduct?.id || preview.id,
      modalProduct?.path || preview.path,
      selectedOptions,
    )

    const targetUrl = new URL(href, window.location.origin)
    const currentUrl = new URL(window.location.href)
    const sameDocument = (
      targetUrl.pathname === currentUrl.pathname
      && targetUrl.search === currentUrl.search
    )

    onClose()

    if (sameDocument) {
      window.location.href = targetUrl.pathname + targetUrl.search + targetUrl.hash
      window.setTimeout(() => {
        window.location.reload()
      }, 10)
      return
    }

    window.location.href = targetUrl.toString()
  }

  const previewImage = preview?.images?.[0]
    ? getImageUrl(preview.images[0], preview.id, 'sm')
    : ''
  const previewImageAlt = preview
    ? getImageAlt(preview.images?.[0], preview.metaTitle)
    : ''

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='md'
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          width: { xs: 'calc(100% - 20px)', sm: 'calc(100% - 32px)' },
          maxWidth: '920px',
          maxHeight: 'calc(100dvh - 24px)',
          m: { xs: 1.25, sm: 2 },
        },
      }}
    >
      <DialogContent sx={{ p: { xs: 2, md: 3 }, position: 'relative', overflowX: 'hidden' }}>
        <IconButton
          onClick={onClose}
          aria-label='Modal schließen'
          sx={{ position: 'absolute', top: 12, right: 12, zIndex: 2 }}
        >
          <CloseIcon />
        </IconButton>

        {loading && !modalProduct && (
          <Box sx={{ minHeight: 360, display: 'grid', gap: 2, alignContent: 'center', justifyItems: 'center' }}>
            {preview && (
              <Box sx={{ width: '100%', display: 'grid', gap: 1.5, justifyItems: 'center', textAlign: 'center' }}>
                <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                  {preview.brandTitel}
                </Typography>
                <Typography variant='h4' sx={{ lineHeight: 1.08 }}>
                  {preview.name}
                </Typography>
                {previewImage && (
                  <Box
                    sx={{
                      display: 'inline-block',
                      lineHeight: 0,
                      maxWidth: 'min(100%, 280px)',
                      position: 'relative',
                    }}
                  >
                    <Box
                      component='img'
                      src={previewImage}
                      alt={previewImageAlt}
                      sx={{
                        display: 'block',
                        maxWidth: '100%',
                        maxHeight: 220,
                        objectFit: 'contain',
                      }}
                    />
                    <AiGeneratedBadge aiGenerated={preview?.images?.[0]?.aiGenerated} size='compact' />
                  </Box>
                )}
              </Box>
            )}
            <CircularProgress />
          </Box>
        )}

        {!loading && !modalProduct && (
          <Box sx={{ minHeight: 220, display: 'grid', placeItems: 'center', gap: 2 }}>
            <Typography>Artikel konnte nicht geladen werden.</Typography>
            <Button onClick={handleOpenArticle} variant='contained'>
              Artikel öffnen
            </Button>
          </Box>
        )}

        {!loading && modalProduct && (
          <Box sx={{ display: 'grid', gap: 3 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.15fr) minmax(320px, .85fr)' },
                gap: 3,
                alignItems: 'start',
              }}
            >
              <Box>
                <Typography variant='body1' sx={{ color: 'text.secondary', mb: .5 }}>
                  {modalProduct.brandTitel}
                </Typography>
                <Typography
                  variant='h4'
                  sx={{
                    mb: 2,
                    fontSize: { xs: '2.15rem', sm: '2.6rem' },
                    lineHeight: 1.08,
                    pr: { xs: 5, sm: 6 },
                  }}
                >
                  {modalProduct.name}
                </Typography>
                <ImagesCarousel
                  product={modalProduct}
                  images={images}
                  mainImageHeight={isMobile ? 180 : 300}
                  disableImageZoomModal
                  mainImageSize='sm'
                />
              </Box>

              <Box
                sx={{
                  boxSizing: 'border-box',
                  display: 'grid',
                  gap: 2,
                  maxWidth: '100%',
                  minWidth: 0,
                  overflow: 'hidden',
                  pr: { xs: 3, sm: 0 },
                  width: '100%',
                  '& > *': {
                    boxSizing: 'border-box',
                    maxWidth: '100%',
                    minWidth: 0,
                  },
                }}
              >
                <AddToCart
                  product={modalProduct}
                  combination={combination}
                  selectedAttributes={selectedAttributes}
                  handleChangeAttribute={handleChangeAttribute}
                />

                <Box
                  sx={{
                    boxSizing: 'border-box',
                    display: 'grid',
                    gap: 1.25,
                    gridTemplateColumns: '1fr',
                    maxWidth: '100%',
                    minWidth: 0,
                    overflow: 'hidden',
                    width: '100%',
                    '& .MuiButton-root': {
                      boxSizing: 'border-box',
                      minHeight: 48,
                      minWidth: 0,
                      whiteSpace: 'normal',
                      width: '100%',
                    },
                  }}
                >
                  <Button
                    variant='outlined'
                    startIcon={<NotesIcon />}
                    onClick={() => setDescriptionOpen(prev => !prev)}
                  >
                    {descriptionOpen ? 'Beschreibung schließen' : 'Beschreibung aufklappen'}
                  </Button>
                  <Button
                    variant='outlined'
                    startIcon={<LaunchIcon />}
                    onClick={handleOpenArticle}
                  >
                    Artikel öffnen
                  </Button>
                  <Button variant='contained' color='primary' onClick={onClose}>
                    Schließen
                  </Button>
                </Box>
              </Box>
            </Box>

            <Collapse in={descriptionOpen}>
              <Box
                sx={{
                  borderTop: '1px solid rgba(15, 23, 42, 0.08)',
                  pt: 2,
                  display: 'grid',
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant='h6' sx={{ mb: 1 }}>
                    Produktdetails
                  </Typography>
                  <Box sx={{ color: 'text.secondary' }}>
                    {parse(modalProduct.description || '', { trim: true })}
                  </Box>
                </Box>

                {modalProduct.descriptionDetails && (
                  <Box>
                    <Typography variant='h6' sx={{ mb: 1 }}>
                      Produktbeschreibung
                    </Typography>
                    <Box sx={{ color: 'text.secondary' }}>
                      {parse(modalProduct.descriptionDetails || '', { trim: true })}
                    </Box>
                  </Box>
                )}
              </Box>
            </Collapse>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default CrossSellProductModal
